import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Check, X, Loader2, ClipboardCheck } from "lucide-react";
import api, { apiError, API_ORIGIN } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const MAX_PHOTO = 8 * 1024 * 1024;
export const photoSrc = (url) => (url ? (/^https?:/.test(url) ? url : `${API_ORIGIN}${url}`) : "");

const entryFor = (value, id) => (value || []).find((c) => c.item_id === id) || { item_id: id, done: false, note: "", photo_id: null };

// Checklist entries to send for one machine: one per template item, in template order.
export function checklistPayload(m, typeMap) {
  const tpl = typeMap[m.machine_type_id]?.checklist || [];
  if (!tpl.length) return undefined;
  return tpl.map((it) => { const e = entryFor(m.checklist, it.id); return { item_id: it.id, done: !!e.done, note: e.note || "", photo_id: e.photo_id || null }; });
}

export function missingRequired(machines, typeMap) {
  const out = [];
  machines.forEach((m, i) => (typeMap[m.machine_type_id]?.checklist || []).forEach((it) => {
    if (it.required && !entryFor(m.checklist, it.id).done) out.push(`${i + 1}: ${it.label}`);
  }));
  return out;
}

function PhotoButton({ entry, onChange, params, testid }) {
  const { t } = useLang();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error(t("Please choose an image"));
    if (file.size > MAX_PHOTO) return toast.error(t("Photo is larger than 8 MB"));
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post("/pro/checklist-photos", fd, { headers: { "Content-Type": "multipart/form-data" }, params, timeout: 120000 }); onChange({ photo_id: data.id, photo_url: data.url }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-2 shrink-0">
      {entry.photo_url && (
        <div className="relative">
          <a href={photoSrc(entry.photo_url)} target="_blank" rel="noreferrer"><img src={photoSrc(entry.photo_url)} alt="" className="w-10 h-10 rounded-lg object-cover border border-zinc-700" data-testid={`${testid}-thumb`} /></a>
          <button type="button" onClick={() => onChange({ photo_id: null, photo_url: null })} data-testid={`${testid}-remove`} className="absolute -top-1.5 -right-1.5 bg-zinc-900 border border-zinc-700 rounded-full p-0.5 text-zinc-400"><X size={10} /></button>
        </div>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-btn`} className="w-10 h-10 rounded-lg bg-[#141416] border border-zinc-800 text-zinc-400 hover:text-[#F26722] flex items-center justify-center" title={t("Add photo")}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
      </button>
    </div>
  );
}

export function ChecklistFill({ template, value, onChange, idx, params }) {
  const { t } = useLang();
  if (!template?.length) return null;
  const update = (id, patch) => {
    const list = template.map((it) => entryFor(value, it.id));
    onChange(list.map((e) => (e.item_id === id ? { ...e, ...patch } : e)));
  };
  return (
    <div className="mt-4 border-t border-zinc-800 pt-3" data-testid={`checklist-fill-${idx}`}>
      <p className="text-xs font-bold text-[#F26722] uppercase tracking-wider flex items-center gap-1.5"><ClipboardCheck size={14} /> {t("Checklist")}</p>
      <div className="space-y-2 mt-2">
        {template.map((it) => {
          const e = entryFor(value, it.id);
          const tid = `checklist-${idx}-${it.id}`;
          return (
            <div key={it.id} className={`rounded-xl p-2.5 border ${it.required && !e.done ? "border-amber-500/30 bg-amber-500/5" : "border-zinc-800 bg-[#141416]"}`}>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={!!e.done} onChange={(ev) => update(it.id, { done: ev.target.checked })} className="w-5 h-5 accent-[#F26722]" data-testid={`${tid}-done`} />
                <span className="text-sm text-white">{it.label}{it.required && <span className="text-[#F26722] font-bold"> *</span>}</span>
              </label>
              <div className="flex gap-2 mt-2">
                <input className="flex-1 min-w-0 bg-[#1c1c1f] border border-zinc-800 text-white rounded-lg px-3 py-2 text-sm placeholder-zinc-600 focus:border-[#F26722] focus:outline-none" placeholder={t("Note / value")} value={e.note || ""} onChange={(ev) => update(it.id, { note: ev.target.value })} data-testid={`${tid}-note`} />
                <PhotoButton entry={e} params={params} testid={`${tid}-photo`} onChange={(p) => update(it.id, p)} />
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-zinc-500 mt-2">* {t("Required before sending")}</p>
    </div>
  );
}

export function ChecklistView({ items, idx }) {
  const { t } = useLang();
  if (!items?.length) return null;
  return (
    <div className="mt-3 border-t border-zinc-800 pt-3" data-testid={`checklist-view-${idx}`}>
      <p className="text-xs font-bold text-[#F26722] uppercase tracking-wider flex items-center gap-1.5"><ClipboardCheck size={14} /> {t("Checklist")}</p>
      <div className="space-y-1.5 mt-2">
        {items.map((c) => (
          <div key={c.item_id} className="flex items-center gap-2.5 bg-[#141416] rounded-lg px-3 py-2" data-testid={`checklist-view-item-${idx}-${c.item_id}`}>
            {c.done ? <Check size={16} className="text-emerald-400 shrink-0" /> : <X size={16} className="text-red-400 shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-zinc-200">{c.label}{c.required && <span className="text-[#F26722]"> *</span>}</p>
              {c.note && <p className="text-xs text-zinc-500">{c.note}</p>}
            </div>
            {c.photo_url && <a href={photoSrc(c.photo_url)} target="_blank" rel="noreferrer"><img src={photoSrc(c.photo_url)} alt="" className="w-10 h-10 rounded-lg object-cover border border-zinc-700" /></a>}
          </div>
        ))}
      </div>
    </div>
  );
}
